/**
 * packages/auth/src/components/VerifyEmailScreen.tsx
 *
 * "Check your email" screen shown after requesting a passwordless sign-in link.
 * Also used as a gate for unverified email/password users (legacy).
 * OAuth users never see this screen.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@bright-web-skeleton/supabase';
import { useAuth } from '../AuthProvider';

export interface VerifyEmailScreenProps {
  /** App name for display */
  appName?: string;
  /** Email to display when authUser is not yet set (pre-session signup) */
  pendingEmail?: string;
  /** Optional back button handler (e.g. return to sign-in form) */
  onBack?: () => void;
  /**
   * Seconds of initial cooldown to apply on mount. Callers that just
   * consumed Supabase's per-identity OTP send interval (i.e. mount this
   * screen right after a successful signInWithOtp) should pass 60 so the
   * Resend button starts disabled and cannot immediately produce a 429.
   */
  initialCooldownSeconds?: number;
}

export const VerifyEmailScreen: React.FC<VerifyEmailScreenProps> = ({
  appName = 'the app',
  pendingEmail,
  onBack,
  initialCooldownSeconds = 0,
}) => {
  const { authUser, logout } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(() =>
    Math.max(0, Math.floor(initialCooldownSeconds ?? 0)),
  );
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      cooldownRef.current = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [cooldown]);

  // Resolve email: prefer authUser, fallback to pendingEmail prop
  const email = authUser?.email || pendingEmail || '';

  const handleResend = useCallback(async () => {
    if (!email || cooldown > 0) return;
    setResending(true);
    setError(null);
    setResent(false);

    try {
      // Use OTP resend for passwordless flow
      const { error: resendError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          shouldCreateUser: true,
        },
      });

      if (resendError) {
        // Supabase per-identity/project throttling. supabase-js exposes
        // `status` and (newer versions) `code`; older versions only carry
        // the human message. Detect all three, arm the cooldown so the
        // button re-disables, and never expose the raw provider message
        // as the primary customer copy.
        const status = (resendError as { status?: number }).status;
        const code = (resendError as { code?: string }).code;
        const rawMsg = resendError.message || '';
        const isThrottled =
          status === 429 ||
          code === 'over_email_send_rate_limit' ||
          code === 'over_request_rate_limit' ||
          /rate limit|only request this after|too many/i.test(rawMsg);

        if (isThrottled) {
          // Best-effort parse of the "after N seconds" hint Supabase
          // sometimes returns in the message body. Fall back to 60.
          const match = rawMsg.match(/after\s+(\d+)\s*seconds?/i);
          const parsed = match ? parseInt(match[1], 10) : NaN;
          const seconds =
            Number.isFinite(parsed) && parsed > 0 && parsed <= 600 ? parsed : 60;
          setCooldown(seconds);
          setError(
            'A sign-in link was recently requested. You can request another one when the timer reaches zero.',
          );
        } else {
          setError(
            rawMsg
              ? `Couldn't resend the sign-in link: ${rawMsg}`
              : "Couldn't resend the sign-in link. Please try again in a moment.",
          );
        }
      } else {
        setResent(true);
        setCooldown(60); // 60s cooldown
      }
    } catch (e) {
      setError('Failed to resend sign-in link. Please try again.');
    } finally {
      setResending(false);
    }
  }, [email, cooldown]);

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, '$1***$3')
    : '';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary"
          >
            <rect width="20" height="16" x="2" y="4" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-foreground">Check your email</h1>

        <p className="text-muted-foreground">
          We sent a secure sign-in link to <strong className="text-foreground">{maskedEmail}</strong>.
          Click the link in your email to sign in to {appName}.
        </p>

        {/* Resend */}
        <div className="space-y-3">
          {resent && (
            <p className="text-sm text-green-600">
              ✓ Sign-in link resent. Check your inbox (and spam folder).
            </p>
          )}
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <button
            onClick={handleResend}
            disabled={resending || cooldown > 0}
            className="w-full py-2.5 px-4 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {resending
              ? 'Sending…'
              : cooldown > 0
                ? `Resend in ${cooldown}s`
                : 'Resend sign-in link'}
          </button>
        </div>

        {/* Back / Sign out */}
        <div className="pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground mb-3">
            Wrong email or need to use a different account?
          </p>
          {onBack ? (
            <button
              onClick={onBack}
              className="text-sm text-primary hover:underline font-medium"
            >
              ← Use a different email
            </button>
          ) : (
            <button
              onClick={logout}
              className="text-sm text-primary hover:underline font-medium"
            >
              Sign out &amp; use a different account
            </button>
          )}
        </div>

        {/* Help text */}
        <p className="text-xs text-muted-foreground">
          If you don't see the email, check your spam folder. Sign-in links expire after 24 hours.
        </p>
      </div>
    </div>
  );
};